import { useEffect, useMemo, useState } from 'react';
import { utmAPI } from '../services/api';
import './UtmGenerator.css';

const strategies = [
    { value: 'weighted', label: 'Weighted Distribution' },
    { value: 'sequential', label: 'Sequential Blocks' },
    { value: 'date_range', label: 'Date Range' },
    { value: 'id_generator', label: 'ID Generator' }
];

const makeId = () => {
    if (window.crypto?.randomUUID) {
        return window.crypto.randomUUID();
    }
    return `col_${Math.random().toString(36).slice(2, 10)}`;
};

const defaultColumn = () => ({
    id: makeId(),
    name: 'new_column',
    strategy: 'weighted',
    data: [{ value: 'default', weight: 100 }]
});

function UtmGenerator() {
    const [columns, setColumns] = useState([]);
    const [templates, setTemplates] = useState([]);
    const [columnPresets, setColumnPresets] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState('');
    const [newTemplateName, setNewTemplateName] = useState('');
    const [selectedFile, setSelectedFile] = useState(null);
    const [previewRows, setPreviewRows] = useState([]);
    const [rowCount, setRowCount] = useState(0);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [generationPolling, setGenerationPolling] = useState(false);
    const [downloadReady, setDownloadReady] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [lastHistory, setLastHistory] = useState(null);
    const [history, setHistory] = useState([]);
    const [downloadingId, setDownloadingId] = useState(null);
    const [statusMessage, setStatusMessage] = useState('');
    const [error, setError] = useState('');
    const [presetInputs, setPresetInputs] = useState({});
    const [selectedColumnPreset, setSelectedColumnPreset] = useState({});
    const historyReady = useMemo(() => {
        const entry = history.find((h) => {
            const cfg = h.columns_config || {};
            return cfg.status === 'ready' || !!cfg.generated_file;
        });
        return entry || null;
    }, [history]);

    useEffect(() => {
        if (historyReady) {
            setLastHistory({ id: historyReady.id, filename: historyReady.filename || 'generated_data.csv' });
            setDownloadReady(true);
            setGenerationPolling(false);
        }
    }, [historyReady]);

    useEffect(() => {
        const bootstrap = async () => {
            try {
                setLoading(true);
                const [templatesRes, presetsRes, historyRes] = await Promise.all([
                    utmAPI.getTableTemplates(),
                    utmAPI.getColumnPresets(),
                    utmAPI.getHistory()
                ]);
                setTemplates(templatesRes.data || []);
                setColumnPresets(presetsRes.data || []);
                setHistory(historyRes.data || []);

                const defaultTemplate = templatesRes.data?.find((tpl) => tpl.is_default) || templatesRes.data?.[0];
                if (defaultTemplate) {
                    setColumns(applyIds(defaultTemplate.columns));
                    setSelectedTemplate(defaultTemplate.id);
                } else {
                    setColumns([defaultColumn()]);
                }
            } catch (err) {
                console.error(err);
                setError(err.response?.data?.detail || 'Failed to load UTM builder data.');
            } finally {
                setLoading(false);
            }
        };

        bootstrap();
    }, []);

    const applyIds = (list = []) =>
        list.map((column) => ({
            ...column,
            id: column.id || makeId()
        }));

    const handleTemplateChange = (templateId) => {
        setSelectedTemplate(templateId);
        const template = templates.find((tpl) => tpl.id === templateId);
        if (template) {
            setColumns(applyIds(template.columns));
            setStatusMessage(`Loaded template "${template.name}"`);
        }
    };

    const handleAddColumn = () => {
        setColumns((prev) => [...prev, defaultColumn()]);
    };

    const updateColumn = (columnId, updater) => {
        setColumns((prev) =>
            prev.map((column) => {
                if (column.id !== columnId) return column;
                return typeof updater === 'function' ? updater(column) : { ...column, ...updater };
            })
        );
    };

    const handleDeleteColumn = (columnId) => {
        setColumns((prev) => prev.filter((column) => column.id !== columnId));
    };

    const moveColumn = (columnId, direction) => {
        setColumns((prev) => {
            const index = prev.findIndex((column) => column.id === columnId);
            if (index === -1) return prev;
            const targetIndex = direction === 'up' ? index - 1 : index + 1;
            if (targetIndex < 0 || targetIndex >= prev.length) return prev;
            const updated = [...prev];
            const [removed] = updated.splice(index, 1);
            updated.splice(targetIndex, 0, removed);
            return updated;
        });
    };

    const handlePresetInputChange = (columnId, value) => {
        setPresetInputs((prev) => ({ ...prev, [columnId]: value }));
    };

    const handlePresetSelectChange = (columnId, value) => {
        setSelectedColumnPreset((prev) => ({ ...prev, [columnId]: value }));
    };

    const compatiblePresets = (columnName) =>
        columnPresets.filter((preset) => preset.target_column === columnName);

    const handleApplyPreset = (columnId) => {
        const presetId = selectedColumnPreset[columnId];
        const preset = columnPresets.find((item) => item.id === presetId);
        if (!preset) return;
        updateColumn(columnId, (column) => ({
            ...column,
            strategy: preset.strategy,
            data: preset.data
        }));
        setStatusMessage(`Applied preset "${preset.name}"`);
    };

    const handleSavePreset = async (column) => {
        const name = presetInputs[column.id];
        if (!name) {
            setError('Enter a preset name first.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            await utmAPI.createColumnPreset({
                name,
                target_column: column.name,
                strategy: column.strategy,
                data: column.data
            });
            const presetsRes = await utmAPI.getColumnPresets();
            setColumnPresets(presetsRes.data || []);
            setPresetInputs((prev) => ({ ...prev, [column.id]: '' }));
            setStatusMessage(`Saved preset "${name}"`);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.detail || 'Failed to save column preset.');
        } finally {
            setSaving(false);
        }
    };

    const handleSaveTemplate = async () => {
        if (!newTemplateName.trim()) {
            setError('Enter a template name first.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            const payload = {
                name: newTemplateName.trim(),
                columns: columns.map((column) => ({
                    ...column,
                    data: column.data
                }))
            };
            await utmAPI.createTableTemplate(payload);
            const templatesRes = await utmAPI.getTableTemplates();
            setTemplates(templatesRes.data || []);
            setNewTemplateName('');
            setStatusMessage(`Saved template "${payload.name}"`);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.detail || 'Failed to save template.');
        } finally {
            setSaving(false);
        }
    };

    const updateTableData = (columnId, rowIndex, key, value) => {
        updateColumn(columnId, (column) => {
            const current = Array.isArray(column.data) ? column.data : [];
            const updatedData = current.map((row, idx) => {
                if (idx !== rowIndex) return row;
                return {
                    ...row,
                    [key]: key === 'weight' || key === 'limit' ? Number(value) : value
                };
            });
            return { ...column, data: updatedData };
        });
    };

    const addDataRow = (columnId) => {
        updateColumn(columnId, (column) => {
            const current = Array.isArray(column.data) ? column.data : [];
            const defaults = column.strategy === 'sequential' ? { value: 'value', limit: 0 } : { value: 'value', weight: 50 };
            return { ...column, data: [...current, defaults] };
        });
    };

    const historyColumnCount = (columnsConfig) => {
        if (Array.isArray(columnsConfig)) return columnsConfig.length;
        if (Array.isArray(columnsConfig?.columns)) return columnsConfig.columns.length;
        return 0;
    };

    const handleDownloadHistory = async (log) => {
        setError('');
        try {
            setDownloadingId(log.id);
            const response = await utmAPI.downloadHistory(log.id);
            const blob = new Blob([response.data], { type: 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = log.filename || 'contacts.csv';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            setStatusMessage('Download started');
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.detail || 'Failed to download CSV.');
        } finally {
            setDownloadingId(null);
        }
    };

    const deleteDataRow = (columnId, rowIndex) => {
        updateColumn(columnId, (column) => {
            const current = Array.isArray(column.data) ? column.data : [];
            return { ...column, data: current.filter((_, idx) => idx !== rowIndex) };
        });
    };

    const renderStrategyEditor = (column) => {
        if (column.strategy === 'date_range') {
            const startValue = column.data?.start_days_ago ?? 30;
            const endValue = column.data?.end_days_ago ?? 0;
            return (
                <div className="utm-two-column">
                    <label>
                        Start Days Ago
                        <input
                            type="number"
                            value={startValue}
                            onChange={(event) =>
                                updateColumn(column.id, {
                                    data: {
                                        start_days_ago: Number(event.target.value),
                                        end_days_ago: endValue
                                    }
                                })
                            }
                        />
                    </label>
                    <label>
                        End Days Ago
                        <input
                            type="number"
                            value={endValue}
                            onChange={(event) =>
                                updateColumn(column.id, {
                                    data: {
                                        start_days_ago: startValue,
                                        end_days_ago: Number(event.target.value)
                                    }
                                })
                            }
                        />
                    </label>
                </div>
            );
        }

        if (column.strategy === 'id_generator') {
            const lengthValue = column.data?.length ?? 32;
            const separatorValue = column.data?.separator ?? '';
            return (
                <div className="utm-two-column">
                    <label>
                        Length
                        <input
                            type="number"
                            min={4}
                            max={64}
                            value={lengthValue}
                            onChange={(event) =>
                                updateColumn(column.id, {
                                    data: { ...column.data, length: Number(event.target.value), separator: separatorValue }
                                })
                            }
                        />
                    </label>
                    <label>
                        Separator
                        <input
                            type="text"
                            value={separatorValue}
                            onChange={(event) =>
                                updateColumn(column.id, {
                                    data: { ...column.data, length: lengthValue, separator: event.target.value }
                                })
                            }
                        />
                    </label>
                </div>
            );
        }

        const headers = column.strategy === 'weighted'
            ? [{ key: 'value', label: 'Value' }, { key: 'weight', label: 'Weight (%)' }]
            : [{ key: 'value', label: 'Value' }, { key: 'limit', label: 'Limit (0 = Max)' }];

        const rows = Array.isArray(column.data) ? column.data : [];

        return (
            <div className="utm-table-editor">
                <div className="utm-table-header">
                    {headers.map((header) => (
                        <span key={header.key}>{header.label}</span>
                    ))}
                    <span />
                </div>
                {rows.map((row, index) => (
                    <div className="utm-table-row" key={`${column.id}-row-${index}`}>
                        {headers.map((header) => (
                            <input
                                key={header.key}
                                type={header.key === 'value' ? 'text' : 'number'}
                                value={row[header.key] ?? ''}
                                onChange={(event) => updateTableData(column.id, index, header.key, event.target.value)}
                            />
                        ))}
                        <button className="btn-ghost" type="button" onClick={() => deleteDataRow(column.id, index)}>
                            ✕
                        </button>
                    </div>
                ))}
                <button className="btn-secondary" type="button" onClick={() => addDataRow(column.id)}>
                    Add Row
                </button>
            </div>
        );
    };

    const handleFileChange = (event) => {
        const file = event.target.files?.[0];
        setSelectedFile(file || null);
    };

    const normalizedColumns = useMemo(
        () =>
            columns.map((column) => ({
                id: column.id,
                name: column.name,
                strategy: column.strategy,
                data: column.data
            })),
        [columns]
    );

    const downloadCsv = async (data) => {
        if (data?.history_id) {
            const response = await utmAPI.downloadHistory(data.history_id);
            const blob = new Blob([response.data], { type: response.headers['content-type'] || 'text/csv' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = data.filename || 'generated_data.csv';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            return;
        }
        if (data?.download_url) {
            const link = document.createElement('a');
            link.href = data.download_url;
            link.download = data.filename || 'generated_data.csv';
            document.body.appendChild(link);
            link.click();
            link.remove();
            return;
        }
        if (data?.csv_base64) {
            const byteCharacters = atob(data.csv_base64);
            const byteNumbers = new Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i += 1) {
                byteNumbers[i] = byteCharacters.charCodeAt(i);
            }
            const byteArray = new Uint8Array(byteNumbers);
            const blob = new Blob([byteArray], { type: data.content_type || 'text/csv' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = data.filename || 'generated_data.csv';
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        }
    };

    const pollForReady = async (historyId) => {
        const maxAttempts = 120; // ~4 minutes @ 2s
        let attempts = 0;
        return new Promise((resolve, reject) => {
            const interval = setInterval(async () => {
                attempts += 1;
                try {
                    const res = await utmAPI.getHistoryEntry(historyId);
                    const cfg = res.data.columns_config || {};
                    const status = cfg.status || '';
                    const ready = status === 'ready' || !!cfg.generated_file;
                    if (ready) {
                        clearInterval(interval);
                        resolve(true);
                    } else if (status && status.toString().startsWith('failed')) {
                        clearInterval(interval);
                        reject(new Error(status));
                    } else if (attempts >= maxAttempts) {
                        clearInterval(interval);
                        reject(new Error('Generation is still processing. Please try downloading from history.'));
                    }
                } catch (err) {
                    console.error(err);
                    clearInterval(interval);
                    reject(err);
                }
            }, 2000);
        });
    };


    const handleDeleteTemplate = async () => {
        if (!selectedTemplate) return;
        if (!window.confirm("Are you sure you want to delete this template?")) return;

        try {
            await utmAPI.deleteTableTemplate(selectedTemplate);
            const templatesRes = await utmAPI.getTableTemplates();
            setTemplates(templatesRes.data || []);
            // Select default after delete
            const defaultTemplate = templatesRes.data?.find((tpl) => tpl.is_default);
            if (defaultTemplate) {
                setSelectedTemplate(defaultTemplate.id);
                setColumns(applyIds(defaultTemplate.columns));
            }
            setStatusMessage("Template deleted");
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to delete template');
        }
    };

    const handleDeletePreset = async (presetId) => {
        if (!window.confirm("Delete this column preset?")) return;
        try {
            await utmAPI.deleteColumnPreset(presetId);
            const presetsRes = await utmAPI.getColumnPresets();
            setColumnPresets(presetsRes.data || []);
            setStatusMessage("Preset deleted");
        } catch (err) {
            setError(err.response?.data?.detail || 'Failed to delete preset');
        }
    };



    const handleGenerate = async () => {
        if (!selectedFile) {
            setError('Upload a CSV template first.');
            return;
        }
        if (normalizedColumns.length === 0) {
            setError('Add at least one column before generating.');
            return;
        }
        setError('');
        setStatusMessage('');
        setGenerating(true);
        try {
            const formData = new FormData();
            formData.append('columns', JSON.stringify(normalizedColumns));
            formData.append('file', selectedFile);
            const response = await utmAPI.generateDataset(formData);
            const data = response.data;
            setPreviewRows(data.preview || []);
            setRowCount(data.row_count || 0);

            if (data.history_id) {
                setLastHistory({ id: data.history_id, filename: data.filename || 'generated_data.csv' });
                // Backend now returns ready file synchronously
                setDownloadReady(true);
                setGenerationPolling(false);
                setStatusMessage('File is ready to download.');
            } else {
                await downloadCsv(data);
            }

            // Refresh history
            const historyRes = await utmAPI.getHistory();
            setHistory(historyRes.data || []);

            setStatusMessage(`Generation started. ${(data.row_count && data.row_count > 0) ? `Generated ${data.row_count} rows.` : ''}`);
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.detail || 'Generation failed.');
        } finally {
            setGenerating(false);
        }
    };

    const handleDownloadClick = async () => {
        if (!lastHistory?.id || !downloadReady) return;
        setDownloading(true);
        setError('');
        try {
            await downloadCsv({ history_id: lastHistory.id, filename: lastHistory.filename });
        } catch (err) {
            console.error(err);
            setError(err.response?.data?.detail || 'Download failed.');
        } finally {
            setDownloading(false);
        }
    };



    const renderPresetManager = () => {
        const userPresets = columnPresets.filter(p => !p.is_default);
        if (userPresets.length === 0) return null;

        return (
            <div className="preset-manager">
                <h4>Your Saved Presets</h4>
                <div className="preset-list">
                    {userPresets.map(preset => (
                        <div key={preset.id} className="preset-item">
                            <span>{preset.name} ({preset.target_column})</span>
                            <button className="btn-ghost danger small" onClick={() => handleDeletePreset(preset.id)}>
                                ×
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    if (loading) {
        return (
            <div className="utm-loading">
                <div className="spinner" />
                <p>Loading builder...</p>
            </div>
        );
    }

    const currentTemplate = templates.find(t => t.id === selectedTemplate);
    const canDeleteTemplate = currentTemplate && !currentTemplate.is_default;

    return (
        <div className="utm-page">
            <header className="utm-header">
                <div>
                    <p className="badge">New</p>
                    <h1>UTM Generator</h1>
                    <p className="subtitle">
                        Rebuild advanced marketing tables without leaving the Blog Writer platform.
                        Configure columns, save templates, and export enriched CSVs.
                    </p>
                </div>
                <div className="header-stats glass">
                    <div>
                        <span className="stat-value">{columns.length}</span>
                        <span className="stat-label">Columns active</span>
                    </div>
                    <div>
                        <span className="stat-value">{rowCount || '—'}</span>
                        <span className="stat-label">Rows generated</span>
                    </div>
                </div>
            </header>

            {error && <p className="utm-alert error">{error}</p>}
            {statusMessage && <p className="utm-alert success">{statusMessage}</p>}

            <div className="utm-layout">
                <section className="utm-panel">
                    <h2>Table Templates</h2>
                    <p className="panel-description">
                        Load the default layout or reuse your saved table scaffolds.
                    </p>
                    <label className="stacked-label">
                        Select template
                        <div style={{ display: 'flex', gap: '8px' }}>
                            <select style={{ flex: 1 }} value={selectedTemplate} onChange={(event) => handleTemplateChange(event.target.value)}>
                                {templates.map((template) => (
                                    <option key={template.id} value={template.id}>
                                        {template.name} {template.is_default ? '(Default)' : ''}
                                    </option>
                                ))}
                            </select>
                            {canDeleteTemplate && (
                                <button className="btn-secondary danger" onClick={handleDeleteTemplate} title="Delete this template">
                                    Delete
                                </button>
                            )}
                        </div>
                    </label>
                    <div className="template-save">
                        <input
                            type="text"
                            placeholder="New template name"
                            value={newTemplateName}
                            onChange={(event) => setNewTemplateName(event.target.value)}
                        />
                        <button className="btn-primary" type="button" disabled={saving} onClick={handleSaveTemplate}>
                            Save Template
                        </button>
                    </div>

                    {renderPresetManager()}
                </section>

                <section className="utm-panel column-panel">
                    {/* ... Existing column builder ... */}
                    <div className="panel-header">
                        <h2>Column Builder</h2>
                        <button className="btn-primary" type="button" onClick={handleAddColumn}>
                            Add Column
                        </button>
                    </div>

                    {columns.length === 0 && <p className="empty-state">No columns configured. Click “Add Column”.</p>}

                    <div className="column-list">
                        {columns.map((column, index) => (
                            <div key={column.id} className="utm-column-card">

                                <div className="column-card-header">
                                    <div className="two-input-row">
                                        <label>
                                            Column Name
                                            <input
                                                type="text"
                                                value={column.name}
                                                onChange={(event) => updateColumn(column.id, { name: event.target.value })}
                                            />
                                        </label>
                                        <label>
                                            Strategy
                                            <select
                                                value={column.strategy}
                                                onChange={(event) => {
                                                    const newStrategy = event.target.value;
                                                    let nextData = column.data;
                                                    if (newStrategy === 'date_range') {
                                                        nextData = { start_days_ago: 30, end_days_ago: 0 };
                                                    } else if (newStrategy === 'id_generator') {
                                                        nextData = { length: 32, separator: '' };
                                                    } else if (!Array.isArray(nextData)) {
                                                        const defaults =
                                                            newStrategy === 'sequential'
                                                                ? [{ value: 'value', limit: 0 }]
                                                                : [{ value: 'value', weight: 100 }];
                                                        nextData = defaults;
                                                    }
                                                    updateColumn(column.id, {
                                                        strategy: newStrategy,
                                                        data: nextData
                                                    });
                                                }}
                                            >
                                                {strategies.map((strategy) => (
                                                    <option key={strategy.value} value={strategy.value}>
                                                        {strategy.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                    </div>

                                    <div className="column-actions">
                                        <button className="btn-ghost" type="button" onClick={() => moveColumn(column.id, 'up')}>
                                            ↑
                                        </button>
                                        <button className="btn-ghost" type="button" onClick={() => moveColumn(column.id, 'down')}>
                                            ↓
                                        </button>
                                        <button className="btn-ghost danger" type="button" onClick={() => handleDeleteColumn(column.id)}>
                                            Delete
                                        </button>
                                    </div>
                                </div>

                                <div className="column-preset-row">
                                    <div>
                                        <label className="stacked-label">
                                            Apply preset
                                            <select
                                                value={selectedColumnPreset[column.id] || ''}
                                                onChange={(event) => handlePresetSelectChange(column.id, event.target.value)}
                                            >
                                                <option value="">Select matching preset</option>
                                                {compatiblePresets(column.name).map((preset) => (
                                                    <option key={preset.id} value={preset.id}>
                                                        {preset.name}
                                                    </option>
                                                ))}
                                            </select>
                                        </label>
                                        <button
                                            className="btn-secondary"
                                            type="button"
                                            disabled={!selectedColumnPreset[column.id]}
                                            onClick={() => handleApplyPreset(column.id)}
                                        >
                                            Apply
                                        </button>
                                    </div>
                                    <div>
                                        <label className="stacked-label">
                                            Save as preset
                                            <input
                                                type="text"
                                                placeholder="Preset name"
                                                value={presetInputs[column.id] || ''}
                                                onChange={(event) => handlePresetInputChange(column.id, event.target.value)}
                                            />
                                        </label>
                                        <button className="btn-primary" type="button" disabled={saving} onClick={() => handleSavePreset(column)}>
                                            Save
                                        </button>
                                    </div>
                                </div>

                                {renderStrategyEditor(column)}
                            </div>
                        ))}
                    </div>
                </section>
            </div>

            <section className="utm-panel">
                <h2>Generate CSV</h2>
                <p className="panel-description">
                    Upload your baseline CSV to clone row counts. We will append all configured columns; generate first, then download when it turns ready.
                </p>
                <div className="generator-grid">
                    <label className="stacked-label">
                        CSV Template
                        <input type="file" accept=".csv" onChange={handleFileChange} />
                    </label>
                    <div className="button-stack">
                        <button className="btn-primary" type="button" disabled={generating || !selectedFile} onClick={handleGenerate}>
                            {generating ? (
                                <>
                                    <span className="spinner inline" />
                                    Generating...
                                </>
                            ) : (
                                'Generate'
                            )}
                        </button>
                    </div>
                </div>
                {previewRows.length > 0 && (
                    <div className="preview-table">
                        <div className="preview-header">
                            <h3>Preview</h3>
                            <p>{Math.min(previewRows.length, 10)} of {previewRows.length} rows shown</p>
                        </div>
                        <div className="table-scroll">
                            <table>
                                <thead>
                                    <tr>
                                        {Object.keys(previewRows[0]).map((key) => (
                                            <th key={key}>{key}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewRows.slice(0, 10).map((row, rowIndex) => (
                                        <tr key={`preview-${rowIndex}`}>
                                            {Object.keys(previewRows[0]).map((key) => (
                                                <td key={`${rowIndex}-${key}`}>{row[key]}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </section>

            {history.length > 0 && (
                <section className="utm-panel history-panel">
                    <div className="history-header">
                        <div>
                            <p className="eyebrow">Recent datasets</p>
                            <h2>Generation History</h2>
                            <p className="panel-description">Download your past CSV exports with a single click.</p>
                        </div>
                    </div>
                    <div className="history-list">
                        {history.map((log) => {
                            const columnCount = historyColumnCount(log.columns_config);
                            return (
                                <div className="history-row" key={log.id}>
                                    <div className="history-meta">
                                        <div className="history-title">
                                            <div className="history-filename">{log.filename}</div>
                                            <div className="history-date">{new Date(log.created_at).toLocaleString()}</div>
                                        </div>
                                        <div className="history-stats">
                                            <span className="pill">Rows: {log.row_count}</span>
                                            <span className="pill">Columns: {columnCount}</span>
                                        </div>
                                    </div>
                                    <div className="history-actions">
                                        <button
                                            className="btn-download"
                                            onClick={() => handleDownloadHistory(log)}
                                            disabled={downloadingId === log.id}
                                        >
                                            {downloadingId === log.id ? 'Preparing…' : 'Download CSV'}
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </section>
            )}
        </div>
    );
}

export default UtmGenerator;
